/**
 * Where the Team Portal's screens live (`docs/superpowers/specs/2026-09-28-two-portals-design.md`,
 * §4 and §6).
 *
 * The admin domain is the Team Portal's content, so its addresses are spelled here, beside the
 * screens that link to one another, and the zone's route module (`zones/team/team-routes.tsx`)
 * mounts what they name. The alternative — the zone owning the paths and the pages importing them
 * from it — would have every screen here depend on the shell it happens to be drawn in, which is
 * the one direction the two zones are kept from ever needing (the design's §2: separable, as if
 * served from two hostnames).
 *
 * The old `/admin/*` addresses are not spelled here at all. They redirect, prefix for prefix, from
 * `src/legacy-routes.tsx`, which is the only file that still knows them.
 */

export const TEAM_ROOT = '/team';

export const TEAM_PATHS = {
  home: TEAM_ROOT,
  responses: `${TEAM_ROOT}/responses`,
  // SR-470's recycle bin (the owner's Q1 (a)): its own list, not a tab of Responses.
  recycleBin: `${TEAM_ROOT}/recycle-bin`,
  people: `${TEAM_ROOT}/people`,
  // SR-414: a team's and a firm's own lists and record addresses, beside People's persons.
  teams: `${TEAM_ROOT}/teams`,
  firms: `${TEAM_ROOT}/firms`,
  // SR-463: every list's matches for one search, where the top search's Enter leads.
  search: `${TEAM_ROOT}/search`,
  registrations: `${TEAM_ROOT}/registrations`,
  secAdv: `${TEAM_ROOT}/sec-adv`,
  secAdvFirms: `${TEAM_ROOT}/sec-adv/firms`,
  secAdvStatus: `${TEAM_ROOT}/sec-adv/status`,
  rankings: `${TEAM_ROOT}/rankings`,
  compliance: `${TEAM_ROOT}/compliance`,
  // SR-487's number checks (PFR §4): the queue of every number registration could not corroborate.
  numberChecks: `${TEAM_ROOT}/number-checks`,
  tasks: `${TEAM_ROOT}/tasks`,
  lists: `${TEAM_ROOT}/lists`,
  ddCalls: `${TEAM_ROOT}/dd-calls`,
  reinstatement: `${TEAM_ROOT}/reinstatement`,
  // SR-315's Metrics updates (RNK-07): who still owes an edition's round its figures.
  metricsUpdates: `${TEAM_ROOT}/metrics-updates`,
  reports: `${TEAM_ROOT}/reports`,
  // SR-462: the fields an administrator hides from the pickers — a static segment no view id spells.
  reportFields: `${TEAM_ROOT}/reports/fields`,
  imports: `${TEAM_ROOT}/import`,
  importThresholds: `${TEAM_ROOT}/import/thresholds`,
  setup: `${TEAM_ROOT}/setup`,
  users: `${TEAM_ROOT}/setup/users`,
  // Users & roles' second tab — a sibling of `users`, never under it: a user card is addressed by
  // email, and a bare username `roles` would be one.
  roles: `${TEAM_ROOT}/setup/roles`,
  builder: `${TEAM_ROOT}/setup/builder`,
  questions: `${TEAM_ROOT}/setup/builder/questions`,
  options: `${TEAM_ROOT}/setup/builder/options`,
  versions: `${TEAM_ROOT}/setup/builder/versions`,
  cycles: `${TEAM_ROOT}/setup/cycles`,
  criteria: `${TEAM_ROOT}/setup/criteria`,
  escalationLevels: `${TEAM_ROOT}/setup/escalation-levels`,
  staffFields: `${TEAM_ROOT}/setup/staff-fields`,
  staffFieldCatalogue: `${TEAM_ROOT}/setup/staff-fields/fields`,
  staffFieldForms: `${TEAM_ROOT}/setup/staff-fields/forms`,
  noteInitials: `${TEAM_ROOT}/setup/staff-fields/initials`,
  refresh: `${TEAM_ROOT}/setup/brokercheck-refresh`,
  refreshRuns: `${TEAM_ROOT}/setup/brokercheck-refresh/runs`,
  exportLayouts: `${TEAM_ROOT}/setup/export-layouts`,
  ddTemplates: `${TEAM_ROOT}/setup/dd-templates`,
  // SR-513: what routes a TEAMS registration, one Setup page — the Firms table.
  teamRouting: `${TEAM_ROOT}/setup/team-routing`
} as const;

/** One response's page — its answers pane, the index of the four. */
export function responsePath(responseId: string): string {
  return `${TEAM_PATHS.responses}/${encodeURIComponent(responseId)}`;
}

/**
 * One account's card, by address — encoded as one segment, as it always was here. The route
 * parameter comes back decoded by React Router and is never decoded again (`user-detail-page.tsx`);
 * the API call re-encodes it per `/`-separated segment (`accounts-api.ts`), which is a different
 * rule for a different URL.
 */
export function userPath(email: string): string {
  return `${TEAM_PATHS.users}/${encodeURIComponent(email)}`;
}

/** One Team Portal role, the role list beside it (SR-228). The id is survey-api's GUID, encoded
 *  as one segment; whether it is one is the reader's question (`isRoleId`), asked before any
 *  request. `search` is the list's query, `?` included. */
export function rolePath(roleId: string, search = ''): string {
  return `${TEAM_PATHS.roles}/${encodeURIComponent(roleId)}${search}`;
}

/** A survey version's editor. */
export function versionPath(versionId: string): string {
  return `${TEAM_PATHS.versions}/${encodeURIComponent(versionId)}`;
}

/** A survey version's preview; `search` is the `?cycle=` it renders against, `?` included. */
export function versionPreviewPath(versionId: string, search = ''): string {
  return `${versionPath(versionId)}/preview${search}`;
}

/**
 * One work item in the compliance queue's pane, the list beside it (SR-222, F9's R1). The item,
 * not the person, is the record — a participant can hold several — so the id is the address,
 * encoded as one segment. `search` is the list's query, `?` included, kept so the list beside the
 * pane is the one the item was opened from.
 */
export function workItemPath(workItemId: string, search = ''): string {
  return `${TEAM_PATHS.compliance}/${encodeURIComponent(workItemId)}${search}`;
}

/**
 * One number check in the Number checks queue's pane, the list beside it (SR-487 ruling 15) — the
 * check is the record. Its segment names the participant beside the check (`numberCheckKey`):
 * survey-api reads one check only on its participant's card (`checks[]`, every status), having no
 * route of its own for one, and every place that links to a check holds its participant.
 */
export function numberCheckPath(participantId: string, checkId: string, search = ''): string {
  return `${TEAM_PATHS.numberChecks}/${encodeURIComponent(numberCheckKey(participantId, checkId))}${search}`;
}

/** The same check opened from My tasks (ruling 16), the viewer's list beside it. */
export function myCheckTaskPath(participantId: string, checkId: string, search = ''): string {
  return `${TEAM_PATHS.tasks}/checks/${encodeURIComponent(numberCheckKey(participantId, checkId))}${search}`;
}

/** A check's key — two GUIDs, which no `~` spells, as a Lists task's pair is keyed. */
export function numberCheckKey(participantId: string, checkId: string): string {
  return `${participantId}~${checkId}`;
}

/** The participant and the check a key names, or null for one no key spells. */
export function readNumberCheckKey(key: string): Nullable<{ participantId: string; checkId: string }> {
  const at = key.indexOf('~');
  return at <= 0 || at === key.length - 1
    ? null
    : { participantId: key.slice(0, at), checkId: key.slice(at + 1) };
}

/** The same item opened from My tasks, whose list is the viewer's own under a fixed query. */
export function myTaskPath(workItemId: string, search = ''): string {
  return `${TEAM_PATHS.tasks}/${encodeURIComponent(workItemId)}${search}`;
}

/** A Lists row assigned to the viewer, opened beside My tasks (SR-424): one segment naming the
 *  participant and the survey, since an assignment is one per pair (Lists design D9). */
export function myListTaskPath(participantId: string, survey: string, search = ''): string {
  return `${TEAM_PATHS.tasks}/rows/${encodeURIComponent(myListTaskKey(participantId, survey))}${search}`;
}

/** A DD meeting assigned to the viewer (SR-501), logged beside My tasks: the meeting is the
 *  record, by survey-api's id. */
export function myMeetingTaskPath(interviewId: string, search = ''): string {
  return `${TEAM_PATHS.tasks}/meetings/${encodeURIComponent(interviewId)}${search}`;
}

/** The pair's key — a participant id (a GUID) and a survey code, which no `~` spells. */
export function myListTaskKey(participantId: string, survey: string): string {
  return `${participantId}~${survey}`;
}

/** The pair a key names, or null for one no key spells. */
export function readMyListTaskKey(key: string): Nullable<{ participantId: string; survey: string }> {
  const at = key.indexOf('~');
  return at <= 0 || at === key.length - 1
    ? null
    : { participantId: key.slice(0, at), survey: key.slice(at + 1) };
}

/**
 * One BrokerCheck refresh run's record, the runs list beside it (SR-222, F10's Q1). The id is
 * encoded as one segment like a work item's; whether it names a run at all — a GUID — is the
 * reader's question (`isRefreshRunId`), asked before any request, so an address typed by hand
 * draws "No such run" rather than reaching another route. `search` is the list's query, `?`
 * included.
 */
export function refreshRunPath(runId: string, search = ''): string {
  return `${TEAM_PATHS.refreshRuns}/${encodeURIComponent(runId)}${search}`;
}

/**
 * The compliance queues narrowed to the work items one refresh run raised (SR-222, F10's Q3 —
 * B3): `run` is sent as survey-api's `refreshRunId`, and `view=all`, because a run's items are
 * worth seeing whatever has become of them since. `trigger` narrows to the items one trigger
 * raised, the run's record's per-trigger link. Every tab: a run raises items in any queue.
 */
export function refreshRunQueuePath(runId: string, trigger?: string): string {
  const query = new URLSearchParams({ run: runId, view: 'all' });
  if (trigger !== undefined) query.set('trigger', trigger);
  return `${TEAM_PATHS.compliance}?${query.toString()}`;
}

/** A ranking's tabs, in the order of the process — the scoring input exported, the scored result
 *  imported, the placements, the finalists, the journal — and the list's own setup (its template
 *  and territories). `scoring` and `territories` are the setup's former addresses, redirected. */
export type RankingTab =
  | 'export'
  | 'threshold'
  | 'import'
  | 'placements'
  | 'finalists'
  | 'history'
  | 'setup';

/**
 * One ranking's record, the editions list beside it (SR-231): addressed as survey-api addresses
 * it, by survey, list and year, each encoded as one segment; the placements tab is the record's
 * root. `search` is the tab's own query (`?territory=`), `?` included.
 */
export function rankingPath(
  key: { surveyCode: string; list: string; year: number | string },
  tab: RankingTab = 'placements',
  search = ''
): string {
  const root = [TEAM_PATHS.rankings, key.surveyCode, key.list, String(key.year)]
    .map((part, index) => (index === 0 ? part : encodeURIComponent(part)))
    .join('/');
  return `${tab === 'placements' ? root : `${root}/${tab}`}${search}`;
}

/** A report's three routed tabs (the design's §4.2) — Definition the record's root. */
export type ReportTab = 'definition' | 'preview' | 'runs';

/** The record of a report not saved yet — a segment no GUID spells, so it meets no view. Its
 *  survey is the address's `?survey=`. */
export const NEW_REPORT = 'new';

/**
 * One report's record, the report list beside it (SR-230, the design's §4). The view's id is
 * survey-api's GUID, encoded as one segment; whether it is one is the reader's question
 * (`isListId`), asked before any request. `search` is the list's query, `?` included.
 */
export function reportPath(viewId: string, tab: ReportTab = 'definition', search = ''): string {
  const root = `${TEAM_PATHS.reports}/${encodeURIComponent(viewId)}`;
  return `${tab === 'definition' ? root : `${root}/${tab}`}${search}`;
}

/** One import's record, the imports beside it (the design's §5) — `reportPath`'s rules. The
 *  thresholds' address is a sibling no GUID can spell, so the two never meet. */
export function importPath(importId: string, search = ''): string {
  return `${TEAM_PATHS.imports}/${encodeURIComponent(importId)}${search}`;
}

/** The record of an import not made yet — the wizard (the design's §5.2), drawn where the import
 *  will be: a segment no GUID spells, so it meets no import. Its survey is the address's
 *  `?survey=`, where the list's filter named one. */
export const NEW_IMPORT = 'new';

/** One export layout on Setup, the layouts beside it (the design's §6) — `reportPath`'s rules. */
export function exportLayoutPath(presetId: string, search = ''): string {
  return `${TEAM_PATHS.exportLayouts}/${encodeURIComponent(presetId)}${search}`;
}
